import ReactiveDialog from "../common/ReactiveDialog";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import PhoneInputList, { PhoneInputListHandle } from "./PhoneInputList";
import { useNavigate } from "react-router-dom";
import { useStartExternalConversation } from "services/advisor";
import { ShellCompositionSection } from "../activity/TextMessageCompose";
import useIsMobile from "../hooks/useIsMobile";
import { Phone } from "lucide-react";

type PhoneContact = {
  phoneNumber: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
};

type ConversationStarterProps = {
  onCompletion?: () => void;
  setEnabled?: (enabled: boolean) => void;
  initialPhoneNumber?: string | null;
  initialPhoneDisplayName?: string | null;
  initialPhoneFirstName?: string | null;
  initialPhoneLastName?: string | null;
};

type ConversationStarterHandle = {
  startConversation: (type: "call" | "message") => void;
};

export const ConversationStarter = forwardRef<
  ConversationStarterHandle,
  ConversationStarterProps
>(
  (
    {
      onCompletion,
      setEnabled,
      initialPhoneNumber,
      ...props
    }: ConversationStarterProps,
    ref,
  ) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { request, loading, error } = useStartExternalConversation();
    const [phoneContacts, setPhoneContacts] = useState<PhoneContact[]>([]);
    const phoneInputRef = useRef<PhoneInputListHandle>(null);

    // Initialize with initial phone number if provided
    useEffect(() => {
      if (initialPhoneNumber) {
        setPhoneContacts([
          {
            phoneNumber: initialPhoneNumber,
            displayName: props.initialPhoneDisplayName || "",
            firstName: props.initialPhoneFirstName || "",
            lastName: props.initialPhoneLastName || "",
          },
        ]);
      }
    }, [
      initialPhoneNumber,
      props.initialPhoneDisplayName,
      props.initialPhoneFirstName,
      props.initialPhoneLastName,
    ]);

    const onStart = async (type: "call" | "message") => {
      if (loading || phoneContacts.length === 0) {
        return false;
      }

      const resp = await request({
        contacts: phoneContacts.map((contact) => ({
          phoneNumber: contact.phoneNumber,
          displayName: contact.displayName || "",
          firstName: contact.firstName || "",
          lastName: contact.lastName || "",
        })),
      });

      if (resp?.conversationRef) {
        onCompletion && onCompletion();
        navigate(
          `/inbox/phone/${encodeURIComponent(
            resp.conversationRef,
          )}?creationRedirect=1&type=${type}`,
        );
        return true;
      }
      return false;
    };

    useImperativeHandle(ref, () => ({
      startConversation: onStart,
    }));

    const enabled = !loading && phoneContacts.length > 0;

    useEffect(() => {
      setEnabled && setEnabled(enabled);
    }, [enabled, setEnabled]);

    useEffect(() => {
      // Handle /inbox/phone/create?phone=+15431234567
      if (phoneContacts.length > 0) {
        onStart("message");
      }
    }, []);

    return (
      <Box
        display="flex"
        flexDirection="column"
        paddingTop={isMobile ? "20px" : "0"}
        gap="20px"
        height="100%"
        justifyContent={"space-between"}
      >
        <Box display="flex" flexDirection="column" gap={"20px"}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={"12px"}
            alignItems={"center"}
          >
            <PhoneInputList
              ref={phoneInputRef}
              phoneContacts={phoneContacts}
              setPhoneContacts={setPhoneContacts}
              placeholder="Phone number"
              autoFocus={true}
              disabled={loading}
              disableDropdown={false}
              sx={{
                marginTop: "8px",
                marginBottom: "4px",
                flex: 1,
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && phoneContacts.length > 0) {
                  e.preventDefault();
                  onStart("message");
                }
              }}
            />
            {/* if desktop, create call action! */}
            {!isMobile && (
              <>
                <Button
                  sx={{
                    border: "1px solid #D4D4D4",
                    borderRadius: "5px",
                    padding: "8px 24px !important",
                    color: "#6B6E7B",
                    backgroundColor: "#fff",
                    fontSize: "14px",
                  }}
                  startIcon={<Phone height="20px" width="20px" />}
                  variant="text"
                  size="small"
                  disabled={!enabled}
                  onClick={() => {
                    onStart("call");
                  }}
                >
                  Call
                </Button>
              </>
            )}
          </Box>
          {error && (
            <Box>
              <Alert severity="error">
                <AlertTitle>Error starting conversation</AlertTitle>
                If the phone number is already associated with a family start
                the conversation from the family page.
              </Alert>
            </Box>
          )}
        </Box>
        <ShellCompositionSection
          onFocus={() => onStart("message")}
          placeholder={
            phoneContacts.length > 1 ? "Send message to group" : "Send message"
          }
          disabled={!enabled}
        />
      </Box>
    );
  },
);

interface Props {
  open: boolean;
  onClose: () => void;
}

export default ({ open, onClose }: Props) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const ref = useRef<ConversationStarterHandle>(null);
  return (
    <ReactiveDialog
      open={open}
      onClose={onClose}
      title="New Conversation"
      unpad={true}
      primaryActionEnabled={enabled}
      primaryAction={() => ref.current?.startConversation("call")}
      primaryActionName={<Phone height={24} width={24} stroke="white" />}
    >
      <ConversationStarter
        ref={ref}
        onCompletion={onClose}
        setEnabled={setEnabled}
      />
    </ReactiveDialog>
  );
};
